import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import GameCard from './components/GameCard';
import { GAMES_CATALOG, GAME_CATEGORIES, DIFFICULTY_OPTIONS } from '@/mocks/jeux-data';
import { useGame } from '@/contexts/GameContext';
import type { GameCategory, GameDifficulty } from '@/types/games';

export default function JeuxPage() {
  const { profile } = useGame();
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('Tous');
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>('Tous');

  const filteredGames = useMemo(() => {
    return GAMES_CATALOG.filter((game) => {
      const matchSearch = search.trim() === '' ||
        game.name.toLowerCase().includes(search.toLowerCase()) ||
        game.description.toLowerCase().includes(search.toLowerCase());
      const matchCat = selectedCategory === 'Tous' || game.category.includes(selectedCategory as GameCategory);
      const matchDiff = selectedDifficulty === 'Tous' || game.difficulty === selectedDifficulty;
      return matchSearch && matchCat && matchDiff;
    });
  }, [search, selectedCategory, selectedDifficulty]);

  const totalGames = profile.gamesPlayed;
  const unlockedAchievements = profile.achievements.filter(a => a.unlockedAt).length;
  const totalAchievements = profile.achievements.length;

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-green-50 via-white to-emerald-50 py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
            <i className="ri-gamepad-line"></i>
            Espace Jeux Éducatifs
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-foreground-950 mb-4">
            Apprendre la santé en s&apos;amusant
          </h1>
          <p className="text-base md:text-lg text-foreground-600 max-w-2xl mx-auto mb-8">
            Découvre notre collection de mini-jeux éducatifs conçus pour toute la famille. 
            Anatomie, nutrition, hygiène... apprends tout sur la santé de façon ludique !
          </p>

          {/* Stats bar */}
          <div className="inline-flex flex-wrap items-center gap-4 md:gap-8 bg-background-50/80 backdrop-blur-sm rounded-2xl px-6 py-4 border border-background-200/70">
            <div className="text-center">
              <div className="text-xl md:text-2xl font-bold text-foreground-950">{totalGames}</div>
              <div className="text-xs text-foreground-600">Parties jouées</div>
            </div>
            <div className="w-px h-8 bg-background-200" />
            <div className="text-center">
              <div className="text-xl md:text-2xl font-bold text-accent-500">{unlockedAchievements}/{totalAchievements}</div>
              <div className="text-xs text-foreground-600">Succès</div>
            </div>
            <div className="w-px h-8 bg-background-200" />
            <div className="text-center">
              <div className="text-xl md:text-2xl font-bold text-primary-500">{profile.totalScore.toLocaleString()}</div>
              <div className="text-xs text-foreground-600">Score total</div>
            </div>
          </div>
        </div>
        <div className="absolute top-0 right-0 w-72 h-72 bg-green-200/30 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald-200/20 rounded-full translate-y-1/2 -translate-x-1/2 blur-3xl" />
      </section>

      {/* Filters */}
      <section className="sticky top-[72px] z-20 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-sm"></i>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher un jeu..."
                className="w-full pl-9 pr-4 py-2.5 bg-background-100 border border-background-200 rounded-xl text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500/50"
              />
            </div>

            {/* Category filter */}
            <div className="flex gap-2 overflow-x-auto pb-1 sm:pb-0 scrollbar-none">
              {['Tous', ...GAME_CATEGORIES].map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-3 py-2 rounded-full text-xs font-medium whitespace-nowrap transition-all cursor-pointer ${
                    selectedCategory === cat
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Difficulty filter */}
            <select
              value={selectedDifficulty}
              onChange={(e) => setSelectedDifficulty(e.target.value)}
              className="px-3 py-2.5 bg-background-100 border border-background-200 rounded-xl text-sm text-foreground-950 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500/30"
            >
              <option value="Tous">Toutes difficultés</option>
              {DIFFICULTY_OPTIONS.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {/* Games Grid */}
      <section className="py-8 md:py-12 flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredGames.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-background-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-search-line text-3xl text-foreground-500"></i>
              </div>
              <h3 className="text-lg font-semibold text-foreground-950 mb-2">Aucun jeu trouvé</h3>
              <p className="text-foreground-600 text-sm">Essaye avec d&apos;autres filtres ou mots-clés.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {filteredGames.map((game) => (
                <GameCard
                  key={game.id}
                  game={game}
                  bestScore={profile.bestScores[game.id] || 0}
                  gamesPlayed={profile.lastPlayed[game.id] ? 1 : 0}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Profile section */}
      {totalGames > 0 && (
        <section className="py-8 md:py-12 bg-background-100/50 border-t border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-xl font-bold text-foreground-950 mb-6">Mes succès</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {profile.achievements.map((a) => (
                <div
                  key={a.id}
                  className={`p-3 rounded-xl text-center transition-all ${
                    a.unlockedAt
                      ? 'bg-accent-100 border border-accent-200'
                      : 'bg-background-100 border border-background-200 opacity-50'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2 ${
                    a.unlockedAt ? 'bg-accent-500 text-background-50' : 'bg-background-200 text-foreground-500'
                  }`}>
                    <i className={`${a.icon} text-lg`}></i>
                  </div>
                  <div className="text-xs font-medium text-foreground-950">{a.name}</div>
                  <div className="text-[10px] text-foreground-600 mt-0.5">{a.description}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}